import { registerSW } from 'virtual:pwa-register';
import { addBreadcrumb } from './observability';

const intervalMs = 1_000 * 60 * 1; // 1 minutes

/**
 * __PWA Service Worker Registration__
 *
 * Triggers periodic `update` calls; basically acting as a polling
 * mechanism to ensure the service worker is up to date.
 *
 * Will not force the service worker to claim clients, or skip waiting,
 * but will ensure the service worker is up to date.
 *
 * Extremely useful for SPAs lacking true navigation within
 * the service worker `scope` that may be open for long periods of time,
 * or for users who may not refresh their browser often.
 *
 * For details around this implementation, learn more from the
 * [Vite PWA guide on periodic service worker updates](https://vite-pwa-org.netlify.app/guide/periodic-sw-updates.html#handling-edge-cases).
 *
 * @see https://vite-pwa-org.netlify.app/guide/periodic-sw-updates.html#periodic-service-worker-updates
 * @see https://web.dev/articles/service-worker-lifecycle
 */
export const pollForUpdates = () => {
  registerSW({
    immediate: true,
    onRegisteredSW(serviceWorkerScriptUrl, registration) {
      if (!registration) return;

      setInterval(() => {
        if (registration.installing) return;

        if (!navigator.onLine) return;

        addBreadcrumb({
          type: 'info',
          level: 'info',
          category: 'service-worker',
          message: 'Polling for Service Worker Updates',
        });

        fetch(serviceWorkerScriptUrl, {
          cache: 'no-store',
          headers: { cache: 'no-store', 'cache-control': 'no-cache' },
        })
          .then((resp) => {
            if (resp.status !== 200) {
              throw new Error(
                `Service Worker Request Failed: ${resp.status} ${resp.statusText}`,
              );
            }

            return registration.update();
          })
          .catch((error) => {
            addBreadcrumb({
              type: 'error',
              level: 'error',
              category: 'service-worker',
              message: 'Service Worker Update Failed',
              // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
              data: { error },
            });
          });
      }, intervalMs);
    },
  });
};
